import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';
import { useEditorStore } from '../stores/editorStore.js';
import { walkBlocksContent } from '@blogforge/shared';

/**
 * Shared font plumbing for the block editors (posts AND emails).
 *
 * - Fetches the system font table and keeps it in the editor store.
 * - Computes which fonts the current blocks actually use.
 * - Injects <link rel="preconnect"> + <link rel="stylesheet"> tags for those
 *   fonts into the editor document and removes them again on change/unmount.
 *
 * Both the post editor and the email editor previously duplicated this whole
 * effect chain; this hook is the single reusable implementation.
 */
export function useEditorFonts() {
  const blocks = useEditorStore((s) => s.blocks);
  const setFonts = useEditorStore((s) => s.setFonts);

  const { data: fonts } = useQuery({
    queryKey: ['fonts'],
    queryFn: () => api.fonts.list(),
  });

  // Keep the store's font table in sync with the fetched list.
  useEffect(() => {
    setFonts(fonts ?? []);
    return () => setFonts([]);
  }, [fonts, setFonts]);

  const fontUrlsToLoad = useMemo(() => {
    if (!fonts) return new Set<string>();

    const urls = new Set<string>();

    walkBlocksContent(blocks, (b) => {
      if (b.style?.fontFamily) {
        const font = fonts.find((f) => f.id === b.style!.fontFamily);
        if (font) {
          urls.add(font.url);
        }
      }
      return b;
    });

    return urls;
  }, [blocks, fonts]);

  // Load the fonts used by the blocks into the editor document.
  useEffect(() => {
    if (fontUrlsToLoad.size === 0) return;

    // Add preconnect for Google Fonts domain
    const addPreconnect = () => {
      if (!document.querySelector('link[rel="preconnect"][href="https://fonts.googleapis.com"]')) {
        const preconnect = document.createElement('link');
        preconnect.rel = 'preconnect';
        preconnect.href = 'https://fonts.googleapis.com';
        document.head.appendChild(preconnect);
      }

      if (!document.querySelector('link[rel="preconnect"][href="https://fonts.gstatic.com"]')) {
        const preconnect = document.createElement('link');
        preconnect.rel = 'preconnect';
        preconnect.href = 'https://fonts.gstatic.com';
        preconnect.crossOrigin = 'anonymous';
        document.head.appendChild(preconnect);
      }
    };

    addPreconnect();

    const loadedLinks: HTMLLinkElement[] = [];

    fontUrlsToLoad.forEach((url) => {
      if (!document.querySelector(`link[href="${url}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;

        // Add error handling
        link.onerror = () => {
          console.error(`Failed to load font: ${url}`);
        };

        link.onload = () => {
          console.log(`Font loaded successfully: ${url}`);
        };

        document.head.appendChild(link);
        loadedLinks.push(link);
      }
    });

    return () => {
      loadedLinks.forEach((link) => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
    };
  }, [fontUrlsToLoad]);
}
